import React, { useEffect, useRef, useState } from "react";
import {Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  RotateCw,
  BarChart2,
  PieChart,
  Download,
} from "lucide-react";
import {
  BedrockMonthlyCost,
  BedrockCostSummary,
  BedrockMonthlyCostResponse,
} from "../../../../types";
import { getBedrockMonthlyCost } from "../../../../service/apiService";
import BedrockCostCharts, {
  BedrockCostChartsHandle,
} from "./BedrockCostCharts";


const formatMoney = (amount: number, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(amount || 0);
  } catch {
    return `${(amount || 0).toFixed(4)} ${currency}`;
  }
};

const formatMonthDisplay = (yearMonth: string) => {
  const [year, month] = yearMonth.split("-").map(Number);
  if (!year || !month) return yearMonth;
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toLocaleDateString("default", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};


const exportTableAsCSV = (
  monthly: BedrockMonthlyCost[],
  summary: BedrockCostSummary | null
) => {
  const currency = summary?.currency || "USD";

  // Build header
  const headers = ["Month", `Total Cost (${currency})`, "Status"];

  // Build rows
  const rows = monthly.map((row) => [
    formatMonthDisplay(row.month),
    row.total_cost.toFixed(6),
    row.is_final ? "Final" : "Provisional",
  ]);

  // Add summary row
  if (summary) {
    rows.push(["", "", ""]);
    rows.push([
      "TOTAL (6 months)",
      summary.total_cost.toFixed(6),
      summary.is_final ? "Final" : "Provisional",
    ]);
  }

  const csvContent = [headers, ...rows]
    .map((r) => r.map((cell) => `"${cell}"`).join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "bedrock-cost-monthly.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};


const BedrockCostMonthlyTable = () => {
  const [monthly, setMonthly] = useState<BedrockMonthlyCost[]>([]);
  const [summary, setSummary] = useState<BedrockCostSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "chart">("table");

  // Ref to trigger ZIP download from the charts component
  const chartsRef = useRef<BedrockCostChartsHandle>(null);

  const fetchMonthlyCost = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result: BedrockMonthlyCostResponse = await getBedrockMonthlyCost();
      // Most recent month first
      const sorted = [...(result.monthly || [])].sort((a, b) =>
        a.month > b.month ? -1 : 1
      );
      setMonthly(sorted);
      setSummary(result.summary || null);
    } catch (err) {
      console.error("Error fetching monthly Bedrock cost:", err);
      setError(
        "Failed to fetch monthly Bedrock cost data. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlyCost();
  }, []);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      if (viewMode === "chart") {
        // ZIP of both chart PNGs — mirrors MonthlyTokenUsageTable pattern
        await chartsRef.current?.downloadAllAsZip("bedrock-cost");
      } else {
        // CSV of the monthly table
        exportTableAsCSV(monthly, summary);
      }} catch (err) {
      console.error("Error exporting Bedrock cost data:", err);
      setError("Failed to export data. Please try again later.");
    } finally {
      setIsExporting(false);
    }
  };

  const currency = summary?.currency || "USD";

  return (
    <Disclosure as="div" className="py-2">
      <DisclosureButton className="group flex w-full items-center justify-between">
        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
          Bedrock Cost Report (monthly)
        </span>
        <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
      </DisclosureButton>
      <DisclosurePanel className="mt-4 text-sm text-gray-600">

        {/* ── Toolbar: Export + View toggle ── */}
        <div className="mb-4 flex flex-col md:flex-row items-center
          space-y-4 md:space-y-0 md:space-x-4">

          {/* Export button */}
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="w-full md:w-auto inline-flex justify-center items-center
              py-2 px-4 border border-transparent shadow-sm text-sm font-medium
              rounded-md text-white bg-violet-600 hover:bg-violet-700
              focus:outline-none focus:ring-2 focus:ring-offset-2
              focus:ring-violet-500 disabled:bg-violet-400
              disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <RotateCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="-ml-1 mr-2 h-4 w-4" />
                Export
              </>
            )}
          </button>

          {/* View toggle — mirrors MonthlyTokenUsageTable exactly */}
          <div className="w-full md:w-auto md:ml-auto flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`flex items-center px-4 py-2 text-sm font-medium
                rounded-l-md border ${
                  viewMode === "table"
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
            ><BarChart2 className="-ml-1 mr-2 h-4 w-4" />
              Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode("chart")}
              className={`flex items-center px-4 py-2 text-sm font-medium
                rounded-r-md border ${
                  viewMode === "chart"
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
            >
              <PieChart className="-ml-1 mr-2 h-4 w-4" />
              Charts
            </button>
          </div>
        </div>

        {/* ── Summary card ── */}
        {summary && (
          <div className="mb-6 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-violet-600" />
              <h3 className="text-base font-medium text-gray-900">
                Total Bedrock Cost (last 6 months)
              </h3>
            </div>
            <div className="px-6 py-4">
              <div className="p-4 rounded-lg bg-violet-50 inline-block">
                <p className="text-sm text-violet-600 font-medium">
                  Total Cost
                </p>
                <p className="text-xl font-bold text-violet-700">
                  {formatMoney(summary.total_cost, currency)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Chart view ── */}
        {viewMode === "chart" ? (
          <div className="mt-6">
            <BedrockCostCharts ref={chartsRef} />
          </div>
        ) : (
          /* ── Table view ── */
          <div className="mt-2 overflow-auto rounded-lg border border-gray-200 max-h-[60vh]">
            {isLoading ? (
              <div className="flex items-center space-x-2 p-4">
                <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
                <span>Loading...</span>
              </div>
            ) : error ? (
              <div className="p-4">Error: {error}</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-20">
                  <tr>
                    <th className="sticky left-0 z-30 bg-gray-100 border-r border-gray-200 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Month
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Cost
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {monthly.length > 0 ? (
                    monthly.map((row) => (
                      <tr key={row.month}>
                        <td className="sticky left-0 z-10 bg-white border-r border-gray-200 px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatMonthDisplay(row.month)}
                        </td>
                        <td className="px-6 py-4 text-center whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatMoney(
                            row.total_cost,
                            row.currency || currency
                          )}
                        </td>
                        <td className="px-6 py-4 text-center whitespace-nowrap text-sm">
                          {row.is_final ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Final
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              Provisional
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-6 py-4 text-center text-sm text-gray-500"
                      >
                        No monthly Bedrock cost data available yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </DisclosurePanel>
    </Disclosure>
  );
};

export default BedrockCostMonthlyTable;